import React from 'react';

/**
 * Shared brand loading indicator.
 * Animated green "signal bars" (equalizer) + optional label — used for page and
 * section loading states so every loading screen looks the same across the app.
 *
 * Props:
 *  - label:      text shown under the bars (optional)
 *  - fullScreen: wrap in the rebranded dotted dark background (for route-level loading)
 *  - size:       'sm' | 'md' | 'lg'  (bar size, default 'md')
 *  - className:  extra classes for the centering wrapper
 */
const SIZES = {
  sm: { w: 3, gap: 3, unit: 5 },   // bar width, gap, height unit (px)
  md: { w: 4, gap: 4, unit: 7 },
  lg: { w: 6, gap: 5, unit: 9 }
};

// Ascending bar heights (in height units) — the "signal bars" shape.
const BARS = [2, 3.2, 4.6, 6, 7.6];

const PageLoader = ({ label, fullScreen = false, size = 'md', className = '' }) => {
  const s = SIZES[size] || SIZES.md;

  const bars = (
    <div className="pl-bars" style={{ display: 'flex', alignItems: 'flex-end', gap: `${s.gap}px`, height: `${7.6 * s.unit}px` }}>
      {BARS.map((h, i) => (
        <span
          key={i}
          style={{
            width: `${s.w}px`,
            height: `${h * s.unit}px`,
            borderRadius: `${s.w}px`,
            background: 'linear-gradient(180deg, #23F8B5 0%, #31C590 100%)',
            transformOrigin: 'bottom',
            animation: `pl-eq 1s ease-in-out ${i * 0.12}s infinite`
          }}
        />
      ))}
    </div>
  );

  const content = (
    <div className={`flex flex-col items-center justify-center ${fullScreen ? 'py-16' : 'py-12'} ${className}`}>
      <style>{`
        @keyframes pl-eq {
          0%, 100% { transform: scaleY(0.35); opacity: 0.55; }
          50%      { transform: scaleY(1);    opacity: 1; }
        }
      `}</style>
      {bars}
      {label && (
        <span className="mt-5 text-xs font-semibold uppercase tracking-[0.28em] text-gray-500">
          {label}
        </span>
      )}
    </div>
  );

  if (!fullScreen) return content;

  return (
    <div
      className="relative min-h-screen text-white"
      style={{
        backgroundColor: '#191c23',
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)',
        backgroundSize: '22px 22px'
      }}
    >
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[60vh]">{content}</div>
    </div>
  );
};

export default PageLoader;
